import Layout from "../Layout/Layout";
import { useEffect, useState } from "react";
import { getContestResultList, getContestDataById } from "./utilities/ApiCalls";
import { statusFailure } from "../../src/views/Constant";
import { useParams } from "react-router";
import confetti from "canvas-confetti";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { setPageTitle } from "../utils/titleUtils";

export default function StoryDetails(props) {
  let { id, type } = useParams();

  const location = useLocation();
  useEffect(() => {
    setPageTitle(location.pathname);
  }, [location]);

  const [winnerDetails, setWinnerDetails] = useState([]);
  const [contestData, setContestData] = useState({});
  const [errorState, setErrorState] = useState(null);
  const { t } = useTranslation();
  const { contestName } = location.state || {};
  const [languageResults, setLanguageResults] = useState([]);
  const [judgeResults, setJudgeResults] = useState([]);
  const [resultImage, setResultImage] = useState(null);

  const isValidValue = (value) => {
    if (value === undefined || value === null) return false;
    const normalized = String(value).trim();
    if (!normalized) return false;
    const lower = normalized.toLowerCase();
    return lower !== "na" && lower !== "n/a" && lower !== "n\\a";
  };
  const isValidPrizeMoney = (value) => {
    if (!isValidValue(value)) return false;
    const num = Number(value);
    return !Number.isNaN(num) && num !== 0;
  };

  useEffect(() => {
    let params = {
      id: id,
      type: type,
    };

    const fetchContestData = async () => {
      try {
        const response = await getContestDataById({ id: id });
        if (response.status !== statusFailure) {
          const contest = response.data[0];
          setContestData(contest);

          // If a result image has been uploaded, use image mode — skip dynamic card processing
          if (contest.resultImage) {
            setResultImage(contest.resultImage);
            setLanguageResults([]);
            setJudgeResults([]);
            return;
          }

          setResultImage(null);

          // Get displayResult value (0 = both, 1 = judge only, 2 = auto only)
          const displayResult = contest.displayResult ?? 0;

          // Process Auto Results (languageResults) - only if displayResult is 0 or 2
          if (
            (displayResult === 0 || displayResult === 2) &&
            contest.languages &&
            contest.resultArray
          ) {
            const langResults = contest.languages
              .map((lang) => {
                const entries = (contest.resultArray[lang] || []).filter(
                  (entry) =>
                    (entry.name && entry.name.trim() !== "") ||
                    (entry.userId && entry.userId !== "")
                );
                return entries.length > 0 ? { language: lang, entries } : null;
              })
              .filter(Boolean);
            setLanguageResults(langResults);
          } else {
            setLanguageResults([]);
          }

          // Process Judge Results - only if displayResult is 0 or 1
          if (
            (displayResult === 0 || displayResult === 1) &&
            Array.isArray(contest.judgeResult)
          ) {
            const validJudgeResults = contest.judgeResult.filter(
              (entry) =>
                (entry.name && entry.name.trim() !== "") ||
                (entry.userId && entry.userId !== "")
            );
            setJudgeResults(
              validJudgeResults.length > 0 ? validJudgeResults : []
            );
          } else {
            setJudgeResults([]);
          }
        } else {
          setErrorState(response.message);
          setTimeout(() => {
            setErrorState("");
          }, 3000);
        }
      } catch (error) {
        console.error("Error fetching contest data:", error); // Debugging log
        setErrorState("Failed to fetch contest data");
        setTimeout(() => {
          setErrorState("");
        }, 3000);
      }
    };

    const fetchWinners = async () => {
      try {
        // console.log(params.id);
        const response = await getContestResultList(params);
        // console.log("Contest Results:", response); // Debugging log

        if (response.status !== statusFailure && response.length > 0) {
          const winners = response.slice(0, 3).map((result) => ({
            story: result.story,
            userName: result.userName,
            numberOfLikes: result.number_of_likes,
            winnerTag: contestData.winnerTag, // assuming these fields are in the result
            runnerUpPrizeMoney: contestData.runnerUpPrizeMoney, // assuming these fields are in the result
          }));
          setWinnerDetails(winners);
          // console.log(
          //   "Winner Details:",
          //   winners.map((winner) => winner.userName)
          // ); // Print all winner names
        } else {
          setErrorState(response.message);
          setTimeout(() => {
            setErrorState("");
          }, 3000);
        }
      } catch (error) {
        console.error("Error fetching winners:", error); // Debugging log
        setErrorState("Failed to fetch winners");
        setTimeout(() => {
          setErrorState("");
        }, 3000);
      }
    };

    fetchContestData();
    fetchWinners();

    // Adding confetti animation
    const colors = ["#465C4C", "#8B6F47"]; // Dark green and golden-brown
    const intervalId = setInterval(() => {
      confetti({
        particleCount: 200,
        angle: 60,
        spread: 90,
        origin: { x: 0 },
        colors: colors,
      });
      confetti({
        particleCount: 200,
        angle: 120,
        spread: 90,
        origin: { x: 1 },
        colors: colors,
      });
    }, 1000);

    // Stop confetti animation after 5 seconds
    setTimeout(() => {
      clearInterval(intervalId);
      confetti.reset();
    }, 5000);

    return () => {
      clearInterval(intervalId);
      confetti.reset();
    };
  }, [id, type]);

  return (
    <Layout>
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "var(--color-raw-porcelain, #F4F5F1)",
        }}
      >
        <Root>
          <div className="result-container">
            {resultImage ? (
              /* ── Image mode: only the image, nothing else ── */
              <div className="result-image-container">
                <img
                  src={resultImage}
                  alt={contestData.name || "Contest Result"}
                  className="result-image"
                />
              </div>
            ) : (
              /* ── Dynamic card mode: full header + cards + footer ── */
              <>
                {/* Header Section */}
                <div
                  className="header-section"
                  style={{ backgroundColor: "transparent" }}
                >
                  <p className="announcement-text">ANNOUNCING THE WINNERS</p>
                  <h1 className="congratulations-title">Congratulations!</h1>
                  <div className="separator-line"></div>
                  {(isValidValue(contestName) ||
                    isValidValue(contestData.name) ||
                    isValidValue(contestData.contestName)) && (
                    <h2 className="contest-name-header">
                      {contestName || contestData.name || contestData.contestName}
                    </h2>
                  )}
                </div>

                {/* Judges' Selection Section */}
                {judgeResults.length > 0 && (
                  <div className="section-container">
                    <h2 className="section-title">
                      {isValidValue(contestData.judgeResultTitle)
                        ? contestData.judgeResultTitle
                        : "Judges' Selection"}
                    </h2>

                    {/* Group judge results by language if available, otherwise show as single category */}
                    {judgeResults.some((entry) => entry.language) ? (
                      // Group by language
                      (() => {
                        const groupedByLang = judgeResults.reduce((acc, entry) => {
                          const lang = entry.language || "General";
                          if (!acc[lang]) acc[lang] = [];
                          acc[lang].push(entry);
                          return acc;
                        }, {});

                        return Object.entries(groupedByLang).map(
                          ([language, entries]) => (
                            <div key={language} className="category-group">
                              <div className="category-separator">
                                <div className="separator-line-left"></div>
                                <p className="category-label">
                                  {language.toUpperCase()} CATEGORY
                                </p>
                                <div className="separator-line-right"></div>
                              </div>
                              {entries.map((entry, i) => (
                                <div key={i} className="winner-card">
                                  {isValidValue(entry.name) && (
                                    <h3 className="winner-name">{entry.name}</h3>
                                  )}
                                  {isValidValue(entry.tag) && (
                                    <p className="winner-tagline">{entry.tag}</p>
                                  )}
                                  {isValidPrizeMoney(entry.prizeMoney) && (
                                    <div className="award-container">
                                      <span className="award-label">AWARD:</span>
                                      <span className="award-amount">
                                        ₹{entry.prizeMoney}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )
                        );
                      })()
                    ) : (
                      // Single category (no language grouping)
                      <div className="category-group">
                        {judgeResults.map((entry, i) => (
                          <div key={i} className="winner-card">
                            {isValidValue(entry.name) && (
                              <h3 className="winner-name">{entry.name}</h3>
                            )}
                            {isValidValue(entry.tag) && (
                              <p className="winner-tagline">{entry.tag}</p>
                            )}
                            {isValidPrizeMoney(entry.prizeMoney) && (
                              <div className="award-container">
                                <span className="award-label">AWARD:</span>
                                <span className="award-amount">
                                  ₹{entry.prizeMoney}
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Decorative Star Separator */}
                {judgeResults.length > 0 && languageResults.length > 0 && (
                  <div className="star-separator">✦</div>
                )}

                {/* Community Choice Section */}
                {languageResults.length > 0 && (
                  <div className="section-container">
                    <h2 className="section-title">
                      {isValidValue(contestData.resultTitle)
                        ? contestData.resultTitle
                        : "Community Choice"}
                    </h2>

                    {languageResults.map((langRes) => (
                      <div key={langRes.language} className="category-group">
                        <div className="category-separator">
                          <div className="separator-line-left"></div>
                          <p className="category-label">
                            {langRes.language.toUpperCase()} CATEGORY
                          </p>
                          <div className="separator-line-right"></div>
                        </div>
                        {langRes.entries.map((entry, i) => (
                          <div key={i} className="winner-card">
                            {isValidValue(entry.name) && (
                              <h3 className="winner-name">{entry.name}</h3>
                            )}
                            {isValidValue(entry.tag) && (
                              <p className="winner-tagline">{entry.tag}</p>
                            )}
                            {isValidPrizeMoney(entry.prizeMoney) && (
                              <div className="award-container">
                                <span className="award-label">AWARD:</span>
                                <span className="award-amount">
                                  ₹{entry.prizeMoney}
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}

                {/* Footer Section */}
                <div className="footer-section">
                  <p className="footer-message">
                    Thank you for being part of this literary journey.
                  </p>
                  <a href="/" className="footer-link">
                    Explore Upcoming Challenges →
                  </a>
                </div>
              </>
            )}
          </div>
        </Root>
      </div>
    </Layout>
  );
}

const Root = styled.div`
  min-height: 100vh;
  height: auto;
  background-color: var(--color-tertiary, #f4f5f1);
  color: var(--color-text-tertiary, var(--color-text));
  display: flex;
  width: 100%;

  /* Result Image */
  .result-image-container {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-bottom: 30px;

    @media (max-width: 768px) {
      margin-bottom: 20px;
    }
  }

  .result-image {
    /* Portrait image: never crop, never stretch horizontally beyond its natural width */
    max-width: 600px;
    width: 100%;
    height: auto;        /* lets the full vertical image show without clipping */
    display: block;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

    @media (max-width: 768px) {
      max-width: 100%;
      border-radius: 6px;
    }
  }

  .result-container {
    width: 100%;
    display: flex;
    align-items: center;
    flex-direction: column;
    padding: 30px 20px;

    @media (max-width: 768px) {
      padding: 20px 15px;
    }
  }

  /* Header Section */
  .header-section {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 35px;

    @media (max-width: 768px) {
      margin-bottom: 25px;
    }
  }

  .announcement-text {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--color-primary);
    margin: 0 0 12px 0;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
      sans-serif;

    @media (max-width: 768px) {
      font-size: 10px;
      letter-spacing: 1.5px;
      margin: 0 0 10px 0;
    }
  }

  .congratulations-title {
    font-size: 56px;
    font-weight: 700;
    text-align: center;
    color: var(--color-primary);
    margin: 0 0 12px 0;
    font-family: Georgia, "Times New Roman", serif;

    @media (max-width: 768px) {
      font-size: 36px;
      font-weight: 700;
      margin: 0 0 10px 0;
      line-height: 1.2;
    }
  }

  .separator-line {
    width: 80px;
    height: 2px;
    background-color: var(--color-contest-gold);
    margin: 0 0 12px 0;

    @media (max-width: 768px) {
      width: 60px;
      height: 2px;
      margin: 0 0 10px 0;
    }
  }

  .contest-name-header {
    font-size: 18px;
    font-weight: 400;
    font-style: italic;
    text-align: center;
    color: var(--color-text-tertiary);
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
      sans-serif;

    @media (max-width: 768px) {
      font-size: 14px;
      line-height: 1.4;
    }
  }

  /* Section Container */
  .section-container {
    width: 100%;
    max-width: 800px;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 30px;

    @media (max-width: 768px) {
      margin-bottom: 25px;
      max-width: 100%;
    }
  }

  .section-title {
    font-size: 48px;
    font-weight: 400;
    text-align: center;
    color: var(--color-primary);
    margin: 0 0 12px 0;
    font-family: Georgia, "Times New Roman", serif;

    @media (max-width: 768px) {
      font-size: 32px;
      font-weight: 400;
      margin: 0 0 10px 0;
      line-height: 1.3;
    }
  }

  /* Category Group */
  .category-group {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 20px;

    @media (max-width: 768px) {
      margin-top: 15px;
    }
  }

  .category-separator {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;

    @media (max-width: 768px) {
      margin-bottom: 15px;
    }
  }

  .separator-line-left,
  .separator-line-right {
    flex: 1;
    height: 1px;
    background-color: var(--color-border);
    max-width: 150px;

    @media (max-width: 768px) {
      max-width: 80px;
    }
  }

  .category-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: var(--color-contest-gold);
    margin: 0 15px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
      sans-serif;

    @media (max-width: 768px) {
      font-size: 9px;
      letter-spacing: 1px;
      margin: 0 10px;
    }
  }

  /* Winner Card */
  .winner-card {
    width: 100%;
    max-width: 600px;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 20px;
    padding: 20px;
    background-color: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: 8px;

    @media (max-width: 768px) {
      max-width: 100%;
      padding: 16px;
      margin-bottom: 15px;
      border-radius: 6px;
      border: 1px solid var(--color-border);
    }
  }

  .winner-name {
    font-size: 32px;
    font-weight: 400;
    text-align: center;
    color: var(--color-text-tertiary, var(--color-text));
    margin: 0 0 6px 0;
    font-family: Georgia, "Times New Roman", serif;

    @media (max-width: 768px) {
      font-size: 24px;
      font-weight: 400;
      margin: 0 0 5px 0;
      line-height: 1.3;
    }
  }

  .winner-tagline {
    font-size: 16px;
    font-style: italic;
    text-align: center;
    color: var(--color-text-tertiary, var(--color-text));
    margin: 0 0 12px 0;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
      sans-serif;

    @media (max-width: 768px) {
      font-size: 14px;
      margin: 0 0 10px 0;
      line-height: 1.4;
    }
  }

  .award-container {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background-color: #e8f5e9;
    border-radius: 6px;
    margin-top: 4px;

    @media (max-width: 768px) {
      padding: 6px 12px;
      gap: 6px;
      border-radius: 4px;
    }
  }

  .award-label {
    font-size: 14px;
    font-weight: 500;
    color: #1b5e20;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
      sans-serif;

    @media (max-width: 768px) {
      font-size: 12px;
      font-weight: 500;
    }
  }

  .award-amount {
    font-size: 14px;
    font-weight: 600;
    color: #1b5e20;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
      sans-serif;

    @media (max-width: 768px) {
      font-size: 12px;
      font-weight: 600;
    }
  }

  /* Star Separator */
  .star-separator {
    font-size: 24px;
    color: var(--color-contest-gold);
    text-align: center;
    margin: 20px 0;

    @media (max-width: 768px) {
      font-size: 20px;
      margin: 15px 0;
    }
  }

  /* Footer Section */
  .footer-section {
    width: 100%;
    max-width: 800px;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 35px;

    @media (max-width: 768px) {
      margin-top: 25px;
      max-width: 100%;
    }
  }

  .footer-message {
    font-size: 18px;
    text-align: center;
    color: var(--color-text-tertiary, var(--color-text));
    margin: 0 0 12px 0;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
      sans-serif;

    @media (max-width: 768px) {
      font-size: 15px;
      margin: 0 0 10px 0;
      line-height: 1.5;
      padding: 0 10px;
    }
  }

  .footer-link {
    font-size: 16px;
    color: var(--color-primary);
    text-decoration: underline;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
      sans-serif;
    transition: opacity 0.2s;

    @media (max-width: 768px) {
      font-size: 14px;
    }

    &:hover {
      opacity: 0.8;
    }
  }
`;
